import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, AlertTriangle, Monitor, Key, Network, Box, Filter, ChevronDown } from 'lucide-react';
import api from '../lib/api';
import { parseApiDate } from '../lib/utils';
import toast from 'react-hot-toast';

const STATUS_CONFIG = {
  active:      { label: 'Active',      color: '#10b981', bg: '#d1fae5' },
  inactive:    { label: 'Inactive',    color: '#6b7280', bg: '#f3f4f6' },
  in_repair:   { label: 'In Repair',   color: '#f59e0b', bg: '#fef3c7' },
  retired:     { label: 'Retired',     color: '#9b9b98', bg: '#f3f4f6' },
  lost_stolen: { label: 'Lost/Stolen', color: '#ef4444', bg: '#fee2e2' },
};

const TYPE_ICONS = { monitor: Monitor, key: Key, network: Network, box: Box };

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.active;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: 4, background: cfg.bg, color: cfg.color, fontSize: 11, fontWeight: 600 }}>
      {cfg.label}
    </span>
  );
}

function ExpiryBadge({ date, label }) {
  if (!date) return null;
  const d = new Date(date);
  const now = new Date();
  const daysLeft = Math.ceil((d - now) / (1000 * 60 * 60 * 24));
  const expired = daysLeft <= 0;
  const soon = daysLeft > 0 && daysLeft <= 30;

  if (!expired && !soon) return null;

  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 6px',
      borderRadius: 4, fontSize: 10, fontWeight: 600,
      background: expired ? '#fee2e2' : '#fef3c7',
      color: expired ? '#ef4444' : '#d97706',
    }}>
      <AlertTriangle size={9} />
      {label}: {expired ? `Expired ${Math.abs(daysLeft)}d ago` : `${daysLeft}d left`}
    </span>
  );
}

export default function AssetsPage() {
  const navigate = useNavigate();
  const [assets, setAssets]       = useState([]);
  const [types, setTypes]         = useState([]);
  const [orgs, setOrgs]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType]     = useState('');
  const [filterOrg, setFilterOrg]       = useState('');
  const [showExpiring, setShowExpiring] = useState(false);
  const [expiringCount, setExpiringCount] = useState(0);
  const [alertCount, setAlertCount]     = useState(0);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (filterStatus)        params.set('status',  filterStatus);
    if (filterType)          params.set('type_id', filterType);
    if (filterOrg)           params.set('org_id',  filterOrg);
    if (search.trim())       params.set('search',  search.trim());
    if (showExpiring)        params.set('expiring', 'true');

    api.get(`/assets?${params}`).then(d => setAssets(d.assets || [])).catch(e => toast.error(e.message)).finally(() => setLoading(false));
  }, [filterStatus, filterType, filterOrg, search, showExpiring]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/assets/types').then(d => setTypes(d.types || [])).catch(() => {});
    api.get('/orgs').then(d => setOrgs(d.orgs || [])).catch(() => {});
    api.get('/assets/alert-count').then(d => setAlertCount(d.count || 0)).catch(() => {});
    // Count expiring
    api.get('/assets/expiring').then(d => setExpiringCount((d.assets || []).length)).catch(() => {});
  }, []);

  function getTypeIcon(icon) {
    const Icon = TYPE_ICONS[icon] || Box;
    return Icon;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div className="page-header">
        <h2>Assets</h2>
        <button className="btn btn-primary" onClick={() => navigate('/assets/new')}><Plus size={14} /> New Asset</button>
      </div>

      <div className="page-body">

      {/* Expiry alert banner */}
      {expiringCount > 0 && (
        <div style={{
          background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 8,
          padding: '10px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <AlertTriangle size={15} style={{ color: '#d97706' }} />
            <strong>{expiringCount} asset{expiringCount !== 1 ? 's' : ''}</strong> with expiring or expired warranties/licenses
          </div>
          <button className="btn btn-secondary btn-sm" onClick={() => { setShowExpiring(s => !s); }}>
            {showExpiring ? 'Show all' : 'Show expiring only'}
          </button>
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input className="form-input" style={{ paddingLeft: 32 }} placeholder="Search assets..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="form-select" style={{ width: 140 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All Statuses</option>
          {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select className="form-select" style={{ width: 160 }} value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="">All Types</option>
          {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <select className="form-select" style={{ width: 180 }} value={filterOrg} onChange={e => setFilterOrg(e.target.value)}>
          <option value="">All Organizations</option>
          {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
      </div>

      {/* Assets table */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}><div className="spinner" /></div>
      ) : assets.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)' }}>
          <Box size={32} style={{ marginBottom: 12, opacity: 0.3 }} />
          <div style={{ fontSize: 14 }}>No assets found</div>
          <button className="btn btn-primary btn-sm" style={{ marginTop: 12 }} onClick={() => navigate('/assets/new')}>Add first asset</button>
        </div>
      ) : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border)' }}>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em' }}>Asset</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em' }}>Type</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em' }}>Organization</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em' }}>Status</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em' }}>Expiry</th>
              </tr>
            </thead>
            <tbody>
              {assets.map(a => {
                const Icon = getTypeIcon(a.type_icon);
                return (
                  <tr key={a.id}
                    style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                    onClick={() => navigate(`/assets/${a.id}`)}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-subtle)'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}
                  >
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 32, height: 32, borderRadius: 6, background: a.type_color ? `${a.type_color}20` : '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <Icon size={15} style={{ color: a.type_color || '#6b7280' }} />
                        </div>
                        <div>
                          <div style={{ fontWeight: 500, fontSize: 13 }}>{a.name}</div>
                          {a.asset_tag && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>#{a.asset_tag}</div>}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '12px 14px', fontSize: 13, color: 'var(--text-muted)' }}>
                      {a.type_name || '—'}
                    </td>
                    <td style={{ padding: '12px 14px', fontSize: 13 }}>{a.org_name || '—'}</td>
                    <td style={{ padding: '12px 14px' }}><StatusBadge status={a.status} /></td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <ExpiryBadge date={a.warranty_expiry} label="Warranty" />
                        <ExpiryBadge date={a.license_expiry}  label="License"  />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: 12, fontSize: 12, color: 'var(--text-muted)' }}>
        {assets.length} asset{assets.length !== 1 ? 's' : ''}
      </div>

      </div>{/* page-body */}
    </div>
  );
}
